/**
 * Payment Page
 * Main page for payment gateway integration with manufacturer dashboard
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui';
import { PaymentMethodSelector } from '../components/PaymentMethodSelector';
import { UPIPaymentInterface } from '../components/UPIPaymentInterface';
import { paymentService, PaymentMethod, PaymentOrder } from '../services/payment.service';
import { NavigationProvider } from '../contexts/NavigationContext';

type PaymentStep = 'select' | 'process' | 'complete';

const PaymentPageContent: React.FC = () => {
  const navigate = useNavigate();

  
  const [currentStep, setCurrentStep] = useState<PaymentStep>('select');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [paymentOrder, setPaymentOrder] = useState<PaymentOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const methods = await paymentService.getPaymentMethods();
      setPaymentMethods(methods);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayment = async () => {
    if (!selectedMethod || !amount) {
      setError('Please select a payment method and enter an amount');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const order = await paymentService.initiatePayment(
        amount,
        selectedMethod.type,
        'Credit top-up for Pureaum platform'
      );

      setPaymentOrder(order);
      setCurrentStep('process');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentComplete = (success: boolean, error?: string) => {
    if (success) {
      setCurrentStep('complete');
    } else {
      setError(error || 'Payment failed');
      setCurrentStep('select');
      setPaymentOrder(null);
    }
  };

  const handleCancel = () => {
    if (paymentOrder) {
      paymentService.cancelPayment(paymentOrder.orderId).catch(console.error);
    }
    setCurrentStep('select');
    setPaymentOrder(null);
    setError(null);
  };

  const handleBackToDashboard = () => {
    navigate('/manufacturer');
  };

  if (loading && paymentMethods.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add Credits</h1>
          <p className="text-sm text-gray-600 mt-1">
            Top up your account to use Pureaum platform services
          </p>
        </div>
      </div>

      {/* Main Content */}
      <section>
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center">
              {/* Step 1 */}
              <div className={`flex items-center ${currentStep === 'select' ? 'text-blue-600' : 'text-green-600'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep === 'select' ? 'bg-blue-100' : 'bg-green-100'
                }`}>
                  {currentStep === 'select' ? '1' : '✓'}
                </div>
                <span className="ml-2 text-sm font-medium">Select Method</span>
              </div>

              {/* Connector */}
              <div className={`w-16 h-0.5 mx-4 ${
                ['process', 'complete'].includes(currentStep) ? 'bg-green-300' : 'bg-gray-300'
              }`}></div>

              {/* Step 2 */}
              <div className={`flex items-center ${
                currentStep === 'process' ? 'text-blue-600' : 
                currentStep === 'complete' ? 'text-green-600' : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep === 'process' ? 'bg-blue-100' : 
                  currentStep === 'complete' ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {currentStep === 'complete' ? '✓' : '2'}
                </div>
                <span className="ml-2 text-sm font-medium">Payment</span>
              </div>

              {/* Connector */}
              <div className={`w-16 h-0.5 mx-4 ${
                currentStep === 'complete' ? 'bg-green-300' : 'bg-gray-300'
              }`}></div>

              {/* Step 3 */}
              <div className={`flex items-center ${
                currentStep === 'complete' ? 'text-green-600' : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep === 'complete' ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {currentStep === 'complete' ? '✓' : '3'}
                </div>
                <span className="ml-2 text-sm font-medium">Complete</span>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-red-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Payment Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        {currentStep === 'select' && (
          <PaymentMethodSelector
            methods={paymentMethods}
            selectedMethod={selectedMethod}
            amount={amount}
            onMethodSelect={setSelectedMethod}
            onAmountChange={setAmount}
            onProceed={handleProceedToPayment}
            loading={loading}
          />
        )}

        {currentStep === 'process' && paymentOrder && selectedMethod && (
          <div>
            {selectedMethod.type === 'UPI' && (
              <UPIPaymentInterface
                orderId={paymentOrder.orderId}
                amount={amount}
                qrCode={paymentOrder.qrCode}
                upiLink={paymentOrder.upiLink}
                onPaymentComplete={handlePaymentComplete}
                onCancel={handleCancel}
              />
            )}

            {selectedMethod.type === 'CARD' && (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Card Payment</h3>
                <p className="text-gray-600 mb-4">
                  You will be redirected to the secure payment gateway to complete your card payment.
                </p>
                {paymentOrder.redirectUrl && (
                  <Button
                    onClick={() => window.location.href = paymentOrder.redirectUrl!}
                    className="mb-4"
                  >
                    Proceed to Payment Gateway
                  </Button>
                )}
                <div className="mt-4">
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel Payment
                  </Button>
                </div>
              </div>
            )}

            {selectedMethod.type === 'NET_BANKING' && (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Net Banking</h3>
                <p className="text-gray-600 mb-4">
                  You will be redirected to your bank's secure login portal to complete the payment.
                </p>
                {paymentOrder.redirectUrl && (
                  <Button
                    onClick={() => window.location.href = paymentOrder.redirectUrl!}
                    className="mb-4"
                  >
                    Proceed to Bank Portal
                  </Button>
                )}
                <div className="mt-4">
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel Payment
                  </Button>
                </div>
              </div>
            )}

            {selectedMethod.type === 'BANK_TRANSFER' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Transfer Details</h3>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Account Name</label>
                      <p className="text-sm text-gray-900">Pureaum Technologies Pvt Ltd</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Account Number</label>
                      <p className="text-sm text-gray-900">1234567890</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">IFSC Code</label>
                      <p className="text-sm text-gray-900">HDFC0001234</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                      <p className="text-sm text-gray-900">HDFC Bank</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700">Reference Number</label>
                    <p className="text-sm text-gray-900 font-mono bg-white px-2 py-1 rounded border">
                      {paymentOrder.orderId}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Please include this reference number in your transfer description
                    </p>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    Your account will be credited within 24 hours after we receive the transfer.
                  </p>
                  <Button onClick={handleCancel} variant="outline">
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 'complete' && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h3>
            <p className="text-gray-600 mb-6">
              Your account has been credited with ₹{amount.toLocaleString()}. 
              A receipt has been sent to your registered email address.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={handleBackToDashboard}>
                Back to Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate('/manufacturer/payment-history')}>
                View Payment History
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

const PaymentPage: React.FC = () => {
  return (
    <NavigationProvider>
      <PaymentPageContent />
    </NavigationProvider>
  );
};

export default PaymentPage;
